import Strategy from '@/models/strategy/Strategy'
import State from '@/models/state/State'
import { STRATEGY_DOMAINS } from '@/models/strategy/strategyDomains'
import { STATE_DOMAINS } from '@/models/state/stateDomains'

/**
 * Chargement des données nécessaires aux alertes.
 *
 * Seuls les domaines réellement lus par la détection sont chargés : une alerte ne
 * justifie pas de rapatrier l'intégralité d'une stratégie ou d'un état. La lecture est
 * ponctuelle, déclenchée à l'ouverture de l'application puis à la demande, plutôt que par
 * des abonnements permanents qui pèseraient sur toutes les pages.
 */
const STRATEGY_KEYS = ['staff', 'buildings', 'stuffs', 'tools', 'offers', 'contracts', 'supplier']
const STATE_KEYS = ['results', 'contracts', 'loans', 'buildings', 'stuffs', 'tools', 'staff']

async function loadDomains(registry, keys, documentId, ownerId, periodId, teamId) {
  const entries = await Promise.all(keys.map(async key => {
    const DomainClass = registry[key]
    if (!DomainClass || !documentId) return [key, null]
    const loaded = await DomainClass.getById(documentId)
    return [key, loaded || DomainClass.initOne(ownerId, periodId, teamId)]
  }))

  const domains = {}
  for (const [key, value] of entries) domains[key] = value
  return domains
}

const alertsLoader = {
  STRATEGY_KEYS,
  STATE_KEYS,

  /** Période précédant celle visée, `null` s'il n'y en a pas. */
  previousPeriodId(periods, periodId) {
    const index = periods.findIndex(period => period.id === periodId)
    if (index <= 0) return null
    return periods[index - 1].id
  },

  /** Domaines de stratégie d'une équipe pour la période visée. */
  async loadStrategy(periodId, teamId) {
    if (!periodId || !teamId) return null
    const strategyId = Strategy.makeStrategyId(periodId, teamId)
    return loadDomains(STRATEGY_DOMAINS, STRATEGY_KEYS, strategyId, strategyId, periodId, teamId)
  },

  /**
   * Domaines de l'état du mois précédent.
   * L'identifiant de l'état est résolu par requête : il ne se déduit pas de la période.
   */
  async loadPreviousState(periods, periodId, teamId) {
    const previousId = this.previousPeriodId(periods, periodId)
    if (!previousId || !teamId) return null

    const states = await State.getByPeriodAndTeam(previousId, teamId)
    const state = states.length ? states[0] : null
    if (!state) return null

    return loadDomains(STATE_DOMAINS, STATE_KEYS, state.id, state.id, previousId, teamId)
  },

  /** Contexte complet d'une équipe, prêt pour la détection. */
  async loadTeamContext({ team, periods, periodId, assets, parameters, alertSettings, depotAreas, depots, members }) {
    const [strategy, previousState] = await Promise.all([
      this.loadStrategy(periodId, team.id),
      this.loadPreviousState(periods, periodId, team.id),
    ])

    return {
      team,
      strategy,
      previousState,
      assets,
      parameters,
      alertSettings,
      depotAreas,
      depots: (depots || []).filter(depot => depot.team === team.id),
      members: members || [],
    }
  },
}

export default alertsLoader
